import type { ComponentType, HTMLAttributes, ReactNode, Ref } from 'react';
import type {
  InputProps as AriaInputProps,
  TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';

import { HelpCircle } from '@untitledui/icons/HelpCircle';
import { InfoCircle } from '@untitledui/icons/InfoCircle';
import { createContext, useContext } from 'react';
import {
  Group as AriaGroup,
  Input as AriaInput,
  TextField as AriaTextField,
} from 'react-aria-components';

import { HintText } from '@/components/base/input/hint-text';
import { Label } from '@/components/base/input/label';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import { cx, sortCx } from '@/utils/cx';

export interface InputBaseProps extends TextFieldProps {
  /** Tooltip message on hover. */
  tooltip?: string;
  /**
   * Input size.
   * @default "sm"
   */
  size?: 'xs' | 'sm' | 'md';
  /** Placeholder text. */
  placeholder?: string;
  /** Class name for the icon. */
  iconClassName?: string;
  /** Class name for the input. */
  inputClassName?: string;
  /** Class name for the input wrapper. */
  wrapperClassName?: string;
  /** Class name for the tooltip. */
  tooltipClassName?: string;
  /** Keyboard shortcut to display. */
  shortcut?: string | boolean;
  ref?: Ref<HTMLInputElement>;
  groupRef?: Ref<HTMLDivElement>;
  /** Icon component to display on the left side of the input. */
  icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
  /** Icon rendered at the trailing end of the input. */
  trailingIcon?: ReactNode;
}

export const InputBase = ({
  ref,
  tooltip,
  shortcut,
  groupRef,
  size = 'sm',
  isInvalid,
  isDisabled,
  icon: Icon,
  placeholder,
  wrapperClassName,
  tooltipClassName,
  inputClassName,
  iconClassName,
  trailingIcon,
  // Omit this prop to avoid invalid HTML attribute warning
  isRequired: _isRequired,
  ...inputProps
}: Omit<InputBaseProps, 'label' | 'hint'>) => {
  const hasBuiltinTrailing = tooltip || isInvalid;
  const hasTrailingIcon = hasBuiltinTrailing || trailingIcon;
  const hasDoubleTrailing = hasBuiltinTrailing && trailingIcon;
  const hasLeadingIcon = Icon;

  // If the input is inside a `TextFieldContext`, use its context to simplify applying styles
  const context = useContext(TextFieldContext);

  const inputSize = context.size ?? size;

  const sizes = sortCx({
    xs: {
      root: cx(
        'px-2.5 py-1.5',
        hasTrailingIcon && !hasDoubleTrailing && 'pr-8',
        hasDoubleTrailing && 'pr-12',
        hasLeadingIcon && 'pl-8',
      ),
      iconLeading: 'left-2.5',
      iconTrailing: 'right-2.5',
      shortcut: 'pr-2',
    },
    sm: {
      root: cx(
        'px-3 py-2',
        hasTrailingIcon && !hasDoubleTrailing && 'pr-9',
        hasDoubleTrailing && 'pr-14',
        hasLeadingIcon && 'pl-10',
      ),
      iconLeading: 'left-3',
      iconTrailing: 'right-3',
      shortcut: 'pr-2.5',
    },
    md: {
      root: cx(
        'px-3.5 py-2.5',
        hasTrailingIcon && !hasDoubleTrailing && 'pr-9.5',
        hasDoubleTrailing && 'pr-14.5',
        hasLeadingIcon && 'pl-10.5',
      ),
      iconLeading: 'left-3.5',
      iconTrailing: 'right-3.5',
      shortcut: 'pr-3',
    },
  });

  return (
    <AriaGroup
      {...{ isDisabled, isInvalid }}
      ref={groupRef}
      className={({ isFocusWithin, isDisabled, isInvalid }) =>
        cx(
          'relative flex w-full flex-row place-content-center place-items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset',

          isFocusWithin && !isDisabled && 'ring-2 ring-brand',

          // Disabled state styles
          isDisabled && 'cursor-not-allowed bg-disabled_subtle ring-disabled',
          'group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled',

          // Invalid state styles
          isInvalid && 'ring-error_subtle',
          'group-invalid:ring-error_subtle',

          // Invalid state with focus-within styles
          isInvalid && isFocusWithin && 'ring-2 ring-error',
          isFocusWithin && 'group-invalid:ring-2 group-invalid:ring-error',

          context.wrapperClassName,
          wrapperClassName,
        )
      }
    >
      {/* Leading icon and Payment icon */}
      {Icon && (
        <Icon
          className={cx(
            'pointer-events-none absolute size-5 text-fg-quaternary',
            isDisabled && 'text-fg-disabled',
            sizes[inputSize].iconLeading,
            context.iconClassName,
            iconClassName,
          )}
        />
      )}

      {/* Input field */}
      <AriaInput
        disabled={isDisabled}
        {...(inputProps as AriaInputProps)}
        ref={ref}
        placeholder={placeholder}
        className={cx(
          'm-0 w-full bg-transparent text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary',
          inputSize === 'xs' ? 'text-sm' : 'text-md',
          isDisabled && 'cursor-not-allowed text-disabled',
          sizes[inputSize].root,
          context.inputClassName,
          inputClassName,
        )}
      />

      {/* Trailing slot: tooltip, error icon, and/or custom trailing icon */}
      {hasTrailingIcon && (
        <div
          className={cx(
            'absolute flex items-center gap-1.5',
            sizes[inputSize].iconTrailing,
          )}
        >
          {tooltip && !isInvalid && (
            <Tooltip title={tooltip} placement="top">
              <TooltipTrigger
                // `TooltipTrigger` inherits the disabled state from the parent
                // form field, which would make the tooltip unopenable exactly
                // when it matters most: explaining why a field is disabled.
                // Same override as `Label`.
                isDisabled={false}
                className={cx(
                  'cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover',
                  context.tooltipClassName,
                  tooltipClassName,
                )}
              >
                <HelpCircle className="size-4" />
              </TooltipTrigger>
            </Tooltip>
          )}
          {isInvalid && (
            <InfoCircle
              className={cx(
                'pointer-events-none size-4 text-fg-error-secondary',
                context.tooltipClassName,
                tooltipClassName,
              )}
            />
          )}
          {trailingIcon}
        </div>
      )}

      {/* Shortcut */}
      {shortcut && (
        <div
          className={cx(
            'pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8',
            sizes[inputSize].shortcut,
          )}
        >
          <span
            className={cx(
              'pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset',
              isDisabled && 'bg-transparent text-disabled',
            )}
            aria-hidden="true"
          >
            {typeof shortcut === 'string' ? shortcut : '⌘K'}
          </span>
        </div>
      )}
    </AriaGroup>
  );
};

InputBase.displayName = 'InputBase';

interface BaseProps {
  /** Label text for the input */
  label?: string;
  /** Helper text displayed below the input */
  hint?: ReactNode;
}

interface TextFieldProps
  extends
    BaseProps,
    AriaTextFieldProps,
    Pick<
      InputBaseProps,
      | 'size'
      | 'wrapperClassName'
      | 'inputClassName'
      | 'iconClassName'
      | 'tooltipClassName'
    > {
  ref?: Ref<HTMLDivElement>;
}

const TextFieldContext = createContext<TextFieldProps>({});

export const TextField = ({ className, ...props }: TextFieldProps) => {
  return (
    <TextFieldContext.Provider value={props}>
      <AriaTextField
        {...props}
        data-input-wrapper
        className={(state) =>
          cx(
            'group flex h-max w-full flex-col items-start justify-start gap-1.5',
            typeof className === 'function' ? className(state) : className,
          )
        }
      />
    </TextFieldContext.Provider>
  );
};

TextField.displayName = 'TextField';

interface InputProps extends InputBaseProps, BaseProps {
  /** Whether to hide required indicator from label */
  hideRequiredIndicator?: boolean;
  /** Class name for the label element */
  labelClassName?: string;
}

export const Input = ({
  size = 'sm',
  placeholder,
  icon: Icon,
  label,
  hint,
  shortcut,
  hideRequiredIndicator,
  labelClassName,
  className,
  ref,
  groupRef,
  tooltip,
  trailingIcon,
  iconClassName,
  inputClassName,
  wrapperClassName,
  tooltipClassName,
  ...props
}: InputProps) => {
  return (
    <TextField
      aria-label={label ? undefined : placeholder}
      {...props}
      className={className}
    >
      {({ isRequired, isInvalid }) => (
        <>
          {label && (
            <Label
              isRequired={
                hideRequiredIndicator ? !hideRequiredIndicator : isRequired
              }
              className={labelClassName}
            >
              {label}
            </Label>
          )}

          <InputBase
            {...{
              ref,
              groupRef,
              size,
              placeholder,
              icon: Icon,
              shortcut,
              trailingIcon,
              iconClassName,
              inputClassName,
              wrapperClassName,
              tooltipClassName,
              tooltip,
            }}
          />

          {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
        </>
      )}
    </TextField>
  );
};

Input.displayName = 'Input';
